"use client";

import { useEffect, useState } from "react";
import { useTranslations } from "next-intl";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MoneyAccountSelect } from "@/components/money/money-account-select";
import { ProofDropField } from "@/components/payments/proof-drop-field";
import { useMoneyContext } from "@/hooks/use-money-context";
import { uploadPaymentProof } from "@/lib/lead-media-api";
import { newMoneyKey } from "@/lib/money-api";
import { settlePayment } from "@/lib/payments-api";
type SettlementTarget = {
  id: string;
  original_payment_id?: string | null;
  currency?: string | null;
  /** The patient the bank's confirmation is filed under; without one there is nowhere to file it. */
  lead_id?: string | null;
  /** The account named when the transfer was recorded — where it is expected to land. */
  money_account_id?: string | null;
};

export function ConfirmPaymentSettlementDialog({ payment, onOpenChange, onSuccess }: { payment: SettlementTarget | null; onOpenChange: (open: boolean) => void; onSuccess: () => void }) {
  const t = useTranslations("money");
  const tc = useTranslations("common");
  const { context, loading, error, allowed } = useMoneyContext();
  const [accountId, setAccountId] = useState("");
  const [date, setDate] = useState("");
  const [key, setKey] = useState(newMoneyKey);
  const [submitting, setSubmitting] = useState(false);
  /**
   * ArtDent 260929 — the bank's confirmation, filed in the same step. Optional
   * on purpose: money that has arrived must never wait on a PDF nobody can
   * find, and the row keeps its "no proof" nudge for the ones without.
   */
  const [proof, setProof] = useState<File | null>(null);
  useEffect(() => { if (payment) { setAccountId(payment.money_account_id ?? ""); setDate(context?.business_date ?? ""); setKey(newMoneyKey()); setProof(null); } }, [payment?.id]);
  useEffect(() => { if (payment && context?.business_date) setDate(current => current || context.business_date); }, [payment?.id, context?.business_date]);
  const ready = !loading && !error && !!context?.activated && allowed;
  /**
   * ArtDent 261003 — a transfer that reached the bank before Cash & banks
   * started is already in the opening balance: it counts as paid and goes
   * into no account, so there is no account to choose.
   */
  const cutover = context?.activation_date ?? null;
  const beforeCutover = !!cutover && !!date && date < cutover;
  const submit = async () => {
    if (!payment || !ready || (!beforeCutover && !accountId) || !date) return;
    setSubmitting(true);
    try {
      const paymentId = payment.original_payment_id ?? payment.id;
      // Settle FIRST, with the account and date chosen here, then file. The
      // other order would let the filed document receive the transfer on its
      // own terms and this request would find nothing left to settle.
      await settlePayment(paymentId, { settlement_date: date, money_account_id: beforeCutover ? undefined : accountId, idempotency_key: key });
      setKey(newMoneyKey());
      toast.success(t("confirmReceivedSuccess"));
      if (proof && payment.lead_id) {
        // Best-effort: the money is received either way.
        try { await uploadPaymentProof(payment.lead_id, proof, paymentId); }
        catch { toast.error(t("proofFileFailed")); }
      }
      onOpenChange(false);
      onSuccess();
    }
    catch (cause) { const e = cause as { response?: { data?: { message?: string; errors?: Record<string, string[]> } } }; toast.error(Object.values(e.response?.data?.errors ?? {})[0]?.[0] ?? e.response?.data?.message ?? t("contextLoadError")); }
    finally { setSubmitting(false); }
  };
  return (
    <Dialog open={!!payment} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle>{t("confirmReceived")}</DialogTitle></DialogHeader>
        {!ready && <p role="alert" className="text-sm text-destructive">{t(context && !context.activated ? "setupPending" : "contextLoadError")}</p>}
        {!beforeCutover && <MoneyAccountSelect label={t("account")} value={accountId} onChange={setAccountId} kind="bank" currency={payment?.currency ?? undefined} disabled={!ready || submitting} />}
        <div className="space-y-2">
          <Label htmlFor="settlement-date">{t("settlementDate")}</Label>
          <Input id="settlement-date" type="date" value={date} max={context?.business_date} onChange={event => setDate(event.target.value)} />
          {beforeCutover && <p className="text-xs text-muted-foreground">{t("beforeCutoverHint", { date: cutover })}</p>}
        </div>
        {payment?.lead_id && (
          <ProofDropField label={t("proofOptional")} hint={t("proofOptionalHint")} value={proof} onChange={setProof} disabled={submitting} />
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{tc("cancel")}</Button>
          <Button onClick={submit} disabled={!ready || (!beforeCutover && !accountId) || !date || submitting}>{t("confirmReceived")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
